import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ledgerService } from "@/services";
import type { LedgerEntry, LedgerEntryType, Member } from "@/services/types";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { StatCard } from "@/components/base/StatCard";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

interface ActivityTabProps {
  member: Member;
}

const TYPE_CONFIG: Record<
  LedgerEntryType,
  { label: string; color: "warning" | "success" | "accent" | "primary" | "danger" | "secondary"; icon: string }
> = {
  installment_due: { label: "Installment Due", color: "warning", icon: "ri-calendar-event-line" },
  payment_received: { label: "Payment Received", color: "success", icon: "ri-check-line" },
  dividend_credit: { label: "Dividend Credit", color: "accent", icon: "ri-gift-line" },
  auction_won: { label: "Auction Won", color: "success", icon: "ri-trophy-line" },
  payout_disbursed: { label: "Payout Disbursed", color: "primary", icon: "ri-bank-card-line" },
  late_fee: { label: "Late Fee", color: "danger", icon: "ri-alarm-warning-line" },
  adjustment: { label: "Adjustment", color: "secondary", icon: "ri-equalizer-line" },
};

const TYPE_OPTIONS = [
  { value: "", label: "All transaction types" },
  { value: "installment_due", label: "Installment Due" },
  { value: "payment_received", label: "Payment Received" },
  { value: "dividend_credit", label: "Dividend Credit" },
  { value: "auction_won", label: "Auction Won" },
  { value: "payout_disbursed", label: "Payout Disbursed" },
  { value: "late_fee", label: "Late Fee" },
  { value: "adjustment", label: "Adjustment / Waiver" },
];

export function ActivityTab({ member }: ActivityTabProps) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null);
  const loading = entries === null;
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await ledgerService.getMemberLedger(member.id);
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load transaction activity.");
      setEntries([]);
    }
  }, [member.id]);

  useEffect(() => {
    load();
  }, [load]);

  const groupOptions = useMemo(() => {
    const opts = [{ value: "", label: "All groups" }];
    const seen = new Set<string>();
    if (entries) {
      for (const e of entries) {
        if (e.groupId && !seen.has(e.groupId)) {
          seen.add(e.groupId);
          opts.push({ value: e.groupId, label: `${e.groupName} (${e.groupCode})` });
        }
      }
    }
    return opts;
  }, [entries]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (groupFilter && e.groupId !== groupFilter) return false;
      if (typeFilter && e.type !== typeFilter) return false;
      if (dateRange.start) {
        const itemDate = new Date(e.date).getTime();
        const start = new Date(dateRange.start).getTime();
        if (itemDate < start) return false;
      }
      if (dateRange.end) {
        const itemDate = new Date(e.date).getTime();
        const end = new Date(dateRange.end).getTime() + 86400000;
        if (itemDate > end) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        const text = `${e.groupName} ${e.groupCode} ${e.reference} ${e.narration} ${e.periodLabel ?? ""}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [entries, groupFilter, typeFilter, dateRange, search]);

  const totals = useMemo(() => {
    if (!entries) return { debits: 0, credits: 0, balance: 0, count: 0 };
    const valid = entries.filter((e) => !e.reversed);
    const debits = valid.reduce((s, e) => s + e.debit, 0);
    const credits = valid.reduce((s, e) => s + e.credit, 0);
    const latestBalance = valid.length > 0 ? valid[valid.length - 1].runningBalance : 0;
    return {
      debits,
      credits,
      balance: latestBalance,
      count: entries.length,
    };
  }, [entries]);

  const columns: DataTableColumn<LedgerEntry>[] = [
    {
      id: "date",
      header: "Date",
      accessor: (r) => <span className="tnum text-xs font-medium text-foreground-900">{formatDate(r.date)}</span>,
      sortValue: (r) => r.date,
      sortable: true,
      width: "110px",
    },
    {
      id: "group",
      header: "Group / Cycle",
      accessor: (r) => (
        <div>
          <span className="font-medium text-foreground-900">{r.groupName}</span>
          <span className="tnum ml-1 text-xs text-foreground-400">({r.groupCode})</span>
          {r.cycle ? (
            <div className="text-xs text-foreground-500">
              Cycle {r.cycle} {r.periodLabel ? `· ${r.periodLabel}` : ""}
            </div>
          ) : null}
        </div>
      ),
      sortValue: (r) => `${r.groupName} ${r.cycle ?? ""}`,
      sortable: true,
    },
    {
      id: "type",
      header: "Type",
      accessor: (r) => {
        const conf = TYPE_CONFIG[r.type] ?? { label: r.type, color: "secondary", icon: "ri-file-line" };
        return (
          <div className="flex flex-col gap-1 items-start">
            <Badge color={conf.color} dot>
              {conf.label}
            </Badge>
            {r.reversed ? <Badge variant="overdue">Reversed</Badge> : null}
          </div>
        );
      },
      sortValue: (r) => r.type,
      sortable: true,
      width: "150px",
    },
    {
      id: "reference",
      header: "Ref #",
      accessor: (r) => <span className="tnum text-xs font-semibold text-primary-700">{r.reference}</span>,
      sortValue: (r) => r.reference,
      sortable: true,
      width: "110px",
    },
    {
      id: "narration",
      header: "Narration",
      accessor: (r) => (
        <span className={`text-xs ${r.reversed ? "text-foreground-400 line-through" : "text-foreground-700"}`}>
          {r.narration}
        </span>
      ),
    },
    {
      id: "debit",
      header: "Debit",
      align: "right",
      accessor: (r) =>
        r.debit > 0 ? (
          <span className={`tnum text-xs font-medium ${r.reversed ? "text-foreground-400 line-through" : "text-danger-600"}`}>
            <CurrencyDisplay amount={r.debit} hideSymbol />
          </span>
        ) : (
          <span className="text-foreground-300">—</span>
        ),
      sortValue: (r) => r.debit,
      sortable: true,
      width: "100px",
      footerValue: (rows) => (
        <span className="text-danger-600 font-semibold">
          <CurrencyDisplay amount={rows.filter((r) => !r.reversed).reduce((s, r) => s + r.debit, 0)} hideSymbol />
        </span>
      ),
    },
    {
      id: "credit",
      header: "Credit",
      align: "right",
      accessor: (r) =>
        r.credit > 0 ? (
          <span className={`tnum text-xs font-medium ${r.reversed ? "text-foreground-400 line-through" : "text-success-700"}`}>
            <CurrencyDisplay amount={r.credit} hideSymbol />
          </span>
        ) : (
          <span className="text-foreground-300">—</span>
        ),
      sortValue: (r) => r.credit,
      sortable: true,
      width: "100px",
      footerValue: (rows) => (
        <span className="text-success-700 font-semibold">
          <CurrencyDisplay amount={rows.filter((r) => !r.reversed).reduce((s, r) => s + r.credit, 0)} hideSymbol />
        </span>
      ),
    },
    {
      id: "balance",
      header: "Balance",
      align: "right",
      accessor: (r) => (
        <span
          className={`tnum text-xs font-semibold ${
            r.runningBalance > 0 ? "text-danger-600" : r.runningBalance < 0 ? "text-success-700" : "text-foreground-700"
          }`}
        >
          <CurrencyDisplay amount={r.runningBalance} hideSymbol />
        </span>
      ),
      sortValue: (r) => r.runningBalance,
      sortable: true,
      width: "110px",
      footerValue: (rows) => {
        const last = rows.filter((r) => !r.reversed);
        const bal = last.length > 0 ? last[last.length - 1].runningBalance : 0;
        return (
          <span className={`tnum text-xs font-bold ${bal > 0 ? "text-danger-600" : bal < 0 ? "text-success-700" : "text-foreground-700"}`}>
            <CurrencyDisplay amount={bal} hideSymbol />
          </span>
        );
      },
    },
  ];

  const hasActiveFilters = Boolean(search || groupFilter || typeFilter || dateRange.start || dateRange.end);

  const clearFilters = () => {
    setSearch("");
    setGroupFilter("");
    setTypeFilter("");
    setDateRange({ start: null, end: null });
  };

  if (error && !entries) {
    return (
      <Card>
        <ErrorState message={error} onRetry={load} />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Activity Summary Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Debits (Dues)"
          value={<CurrencyDisplay amount={totals.debits} hideSymbol tone="danger" />}
          icon="ri-arrow-up-line"
          iconClass="bg-danger-100 text-danger-700"
          tone="danger"
        />
        <StatCard
          label="Total Credits (Won/Paid/Dividends)"
          value={<CurrencyDisplay amount={totals.credits} hideSymbol tone="success" />}
          icon="ri-arrow-down-line"
          iconClass="bg-success-100 text-success-700"
          tone="success"
        />
        <StatCard
          label="Current Balance"
          value={
            <CurrencyDisplay
              amount={totals.balance}
              tone={totals.balance > 0 ? "danger" : totals.balance < 0 ? "success" : "default"}
              hideSymbol
            />
          }
          icon="ri-wallet-3-line"
          iconClass={totals.balance > 0 ? "bg-danger-100 text-danger-700" : totals.balance < 0 ? "bg-success-100 text-success-700" : "bg-primary-100 text-primary-700"}
          tone={totals.balance > 0 ? "danger" : totals.balance < 0 ? "success" : "accent"}
        />
        <StatCard
          label="Total Transactions"
          value={totals.count}
          icon="ri-file-list-3-line"
          iconClass="bg-secondary-100 text-secondary-700"
          tone="default"
        />
      </div>

      {/* Main Ledger Table Card */}
      <Card
        title="Transaction Ledger & Activity"
        subtitle={`Chronological financial entries for ${member.fullName}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              icon={<i className="ri-file-text-line" />}
              onClick={() => navigate(`/reports/statements?memberId=${member.id}`)}
            >
              View Full Statement
            </Button>
          </div>
        }
      >
        {/* Filters Toolbar */}
        <div className="mb-4 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-60">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                type="text"
                placeholder="Search ref, narration, group…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-8 text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
              />
              {loading ? (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center text-primary-600">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                </span>
              ) : search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 flex w-8 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              ) : null}
            </div>
            <div className="w-full sm:w-48">
              <Select value={groupFilter} onChange={setGroupFilter} options={groupOptions} />
            </div>
            <div className="w-full sm:w-48">
              <Select value={typeFilter} onChange={setTypeFilter} options={TYPE_OPTIONS} />
            </div>
            <div className="w-full sm:w-56">
              <DateRangePicker value={dateRange} onChange={setDateRange} placeholder="All Dates" />
            </div>
          </div>

          {hasActiveFilters ? (
            <Button size="sm" variant="ghost" icon={<i className="ri-close-line" />} onClick={clearFilters}>
              Reset filters
            </Button>
          ) : null}
        </div>

        {/* DataTable */}
        {!entries ? (
          <div className="space-y-3 py-4">
            <div className="h-10 animate-pulse rounded bg-background-200" />
            <div className="h-10 animate-pulse rounded bg-background-200" />
            <div className="h-10 animate-pulse rounded bg-background-200" />
          </div>
        ) : (
          <DataTable<LedgerEntry>
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            emptyMessage={hasActiveFilters ? "No matching transactions" : "No transaction activity recorded"}
            emptyDescription={
              hasActiveFilters
                ? "No entries match your search and filter criteria. Try adjusting or clearing filters."
                : "Financial activity (installment demands, payments, dividends, payouts) will appear here once recorded."
            }
            showTotals
            totalLabel="Total across transactions"
            exportable
            exportFilename={`ledger-${member.memberCode}-${member.fullName.replace(/\s+/g, "_")}`}
            defaultPageSize={10}
          />
        )}
      </Card>
    </div>
  );
}